import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { CustomOverlayRef } from '@core/services/custom-overlay-ref';
import { LoginPayload } from '@features/auth/auth.models';
import { AuthService } from '@features/auth/auth.service';
import { IFormBuilder, IFormGroup } from '@rxweb/types';
import { ErrorHandlerModule } from '@shared/error-handler/error-handler.component';
import { AppErrorHandler } from '@shared/error-handler/error-handler.model';
import { InlineLoaderModule } from '@shared/inline-loader/inline-loader.component';
import { InputModule } from '@shared/controls/input/input.component';
import { ModalModule } from '@shared/modal/modal.component';
import { DestroySubject } from '@shared/utils/destroy-subject';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-private-packages-login',
  templateUrl: './private-packages-login.component.html',
  imports: [AsyncPipe, ReactiveFormsModule, ErrorHandlerModule, InlineLoaderModule, InputModule, ModalModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrivatePackagesLoginComponent extends DestroySubject implements OnInit {
  public form: IFormGroup<LoginPayload & { agree: boolean }>;

  public loading: boolean = false;

  public messages$: Subject<AppErrorHandler> = new Subject();

  constructor(
    public modalRef: CustomOverlayRef<void, void>,
    @Inject(UntypedFormBuilder) protected fb: IFormBuilder,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form = this.fb.group<LoginPayload & { agree: boolean }>({
      username: ['', [Validators.required]],
      password: ['', [Validators.required]],
      agree: [false, [Validators.requiredTrue]],
    });
  }

  public onSubmit(): void {
    this.messages$.next(null);

    if (this.form.invalid) {
      return;
    }

    const { username, password } = this.form.getRawValue();

    this.loading = true;
    this.authService
      .loginPrivatePackages({ username, password })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(
        () => this.onSignInResponse(),
        (error: HttpErrorResponse) => this.messages$.next(error)
      );
  }

  // The checkbox can only be ticked through the "I Agree" button in the terms dialog;
  // clicking it while unticked opens the dialog instead. Unticking is always allowed.
  public onAgreeClick(event: Event, terms: HTMLDialogElement): void {
    if (!this.form.value.agree) {
      event.preventDefault();
      terms.showModal();
    }
  }

  private onSignInResponse(): void {
    if (!this.authService.isPrivatePackagesAccount()) {
      this.authService.logout();
      this.messages$.next(['This account is not authorized to view private packages.']);
      return;
    }

    this.modalRef.close();
  }
}
